import React, { useState, forwardRef, useMemo, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import 'react-phone-number-input/style.css';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js/max';
import PhoneInput from 'react-phone-number-input';

const CustomInput = forwardRef(({ value, onChange, error, ...props }, ref) => (
	<TextField
		value={value || ''}
		onChange={(e) => onChange(e.target.value)}
		type="tel"
		inputRef={ref}
		error={!!error}
		helperText={error || undefined}
		{...props}
	/>
));

const PhoneField = ({ value, onChange, name, label, style, disabled, allowFixedLine, defaultCountry  = 'BE', unsupportedCountries, unsupportedCountryMessage, ...props }) => {
	const [number, setNumber] = useState(value);
	const effectiveCountry = useMemo(() => {
		const countryFromValue = value  && isValidPhoneNumber(value) ? parsePhoneNumber(value)?.country : null;
		return countryFromValue || defaultCountry;
	}, [value, defaultCountry]);


	useEffect(() => {
		setNumber(value);
	}, [value]);

	const isUnsupportedCountry = (v) => {
		if (!unsupportedCountries?.length || !isValidPhoneNumber(v)) return false;
		const country = parsePhoneNumber(v).country;
		return unsupportedCountries.includes(country);
	};

	const isValid = v => {
		if (!isValidPhoneNumber(v)) {
			return false;
		}

		if (isUnsupportedCountry(v)) {
			return false;
		}

		const type = parsePhoneNumber(v).getType();

		if (allowFixedLine && type === 'FIXED_LINE') {
			return true;
		}

		if (type === 'MOBILE' || type == 'FIXED_LINE_OR_MOBILE') {
			return true;
		}

		return false;
	};

	const getErrorMessage = (v) => {
		if (!v) return undefined;
		if (!isValidPhoneNumber(v)) return 'Invalid phone number';
		if (isUnsupportedCountry(v)) return unsupportedCountryMessage || 'Phone number from unsupported country';
		if (!isValid(v)) return 'Invalid phone number';
		return undefined;
	};

	const handleChange = (passedValue) => {
		setNumber(passedValue);

		// Remove all white space from phone number so it is stored always in the same format.
		const valueToStore = passedValue?.replace(/ /g, '');
		if(passedValue && isValid(passedValue)) {
			onChange({ target: { name, value: valueToStore } });
		} else {
			onChange({ target: { name, value: '' } });
		}
	};

	return (
		<PhoneInput
			value={number}
			variant="outlined"
			onChange={handleChange}
			fullWidth
			defaultCountry={effectiveCountry}
			inputComponent={CustomInput}
			international={true}
			countryCallingCodeEditable={false}
			error={getErrorMessage(number)}
			type='tel'
			autoComplete="false"
			name={name}
			label={label}
			style={style}
			disabled={disabled}
			placeholder={'+32 499 00 00 00'}
			{...props}
		/>
	);
};

PhoneField.type = 'PhoneField';

export default PhoneField;
